import SvgIcon from '../../../components/layout/icons/Icons';
import { PilotageExemptionApplicationStatusColors, PilotageExemptionApplicationStatusEnum } from '../../../services/SystemNames';
import Moment from 'moment';
import { MiniCard } from '../../../components/layout/card/MiniCard';
import { getPilotAssignmentStatusColor } from '../../pilot/pilot-assignment/helpers/PilotAssignmentHelpers';
import { ArrowElbowDownRight, Eye, Lock } from 'phosphor-react-sc';
import { useEffect, useRef, useState } from 'react';
import PilotageApiRepository from 'repositories/api/PilotageApiRepository';
import PilotApiRepository from '../../../repositories/api/PilotApiRepository';
import { Repository as PilotStationApiRepository } from '@pilot-web/pilot-station';
import SourceApiRepository from '../../../repositories/api/SourceApiRepository';
import { useNavigate, useSearchParams } from 'react-router';
import { scrollCardElementIntoView } from '../../../components/helpers/ElementHelpers';
import { isArrayEmpty, isNumeric } from '../../../components/helpers/ObjectHelpers';
import { SuspenseWithErrorBoundry } from '../../../components/layout/suspense/SuspenseWithErrorBoundry';
import { SuspenseFallback } from '../../../components/layout/suspense/SuspenseFallback';
import { ErrorBoundryFallback } from '../../../components/layout/suspense/ErrorBoundryFallback';
import ContentGrid from '../../../components/layout/ContentGrid';
import { Card } from '../../../components/layout/card/Card';
import { createPilotageCurrentDetailsUrl, createPilotPilotageDetailsUrl } from '../../../components/helpers/CreateUrlHelpers';
import { useInfoNotification, useSuccessNotification } from 'components/hooks/useAppStore';

export const PageStateCurrent = () => {

    const [dataPromise, setDataPromise] = useState(null);
    const addInfoNotification = useInfoNotification();

    const fetchStationsFromApi = async () => {
        const response = await PilotStationApiRepository.getPreferencesFavouriteStations();
        return await response.json();
    }

    const fetchStations = async () => {
        return await fetchStationsFromApi();  
    }

    const fetchData = async () => {
        const stations = await fetchStations();
        if (isArrayEmpty(stations)) {
            addInfoNotification(
                "Du har ingen valgte losstasjoner.",
            );
            return [];
        }
        const response = await PilotApiRepository.searchCurrentPilotagesAsync(stations.map((ps) => ps.departmentID));
        return await response.json();
    }

    const initialize = () => {
        setDataPromise(fetchData());
    }

    useEffect(() => {
        initialize();
    }, []);

    return (
        <ContentGrid
            title="Alle oppdrag"
            titleIcon="anchor"
            onPullToRefresh={initialize}>
            <SuspenseWithErrorBoundry
                suspenseFallback={<SuspenseFallback />}
                errorBoundryFallback={<ErrorBoundryFallback mode="text" text="Kunne ikke laste inn oppdrag..." />}>
                {
                    dataPromise &&
                    <PageStateCurrentContent
                        dataPromise={dataPromise}
                    />
                }
            </SuspenseWithErrorBoundry>
        </ContentGrid>
    )
}

const PageStateCurrentContent = ({
    dataPromise,
}) => {

    const [pilotages, setPilotages] = useState([]);
    useEffect(() => {
        dataPromise.then(setPilotages);
    }, [dataPromise]);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pilotData, setPilotData] = useState(null);
    const loggedInPersonId = useRef(0);

    useEffect(() => {
        getLoggedInPerson();

        const pilotageIdParam = searchParams.get("pilotageId");
        if (isNumeric(pilotageIdParam)) {
            scrollCardElementIntoView(pilotageIdParam
            );
        }

    }, [pilotages]);

    return (
        <div className="current-pilotages">
            {
                pilotages.map((pilotage, index) =>
                    <Pilotage key={index} pilotage={pilotage} onClick={onClick} setPilotages={setPilotages} pilotData={pilotData} />
                )
            }
        </div>
    )

    async function getLoggedInPerson() {
        const response = await SourceApiRepository.getWhoAmIAsync();
            const data = response instanceof Response ? await response.json() : response;
            setPilotData(data);
            loggedInPersonId.current = data.personId;
    }

    function onClick(pilotage) {
        let url = createPilotPilotageDetailsUrl(pilotage.pilotageId);

        if (
            !pilotage.pilotagePilotsInformation.some(
                (p) => p.pilotPersonId === loggedInPersonId.current,
            )
        ) {
            url = createPilotageCurrentDetailsUrl(pilotage.pilotageId);
        }

        setSearchParams({
            pilotageId: pilotage.pilotageId
        }, { replace: true });

        navigate(url);
    }
}

const Pilots = ({ pilotagePilots }) => {
    return (
        pilotagePilots.map((pilotagePilot, index) =>
            <MiniCard
                key={index}
                item={{
                    borderColor: getPilotAssignmentStatusColor(pilotagePilot),
                    text: `${pilotagePilot.initials}`,
                    isExaminer: pilotagePilot.isExaminer,
                    isTrainee: pilotagePilot.isTrainee,
                    isApprentice: pilotagePilot.isApprentice
                }}
            />
        )
    )
};

export const Pilotage = ({ pilotage, onClick, setPilotages, pilotData }) => {
    const [canAccept, setCanAccept] = useState(getCanAccept());
    const addSuccessNotification = useSuccessNotification();

    const handleAccept = async (pilotageId) => {
        if (!pilotData) return;
        var response = await PilotageApiRepository.createSelfAssignedPilotage(pilotageId);
        if (response.ok === true) {
            setPilotages(prev =>
                prev.map(p =>
                    p.pilotageId === pilotageId
                        ? {
                            ...p,
                            pilotagePilotsInformation: [
                                ...p.pilotagePilotsInformation,
                                {
                                    pilotPersonId: pilotData.personID,
                                    initials: pilotData.initials,
                                    pilotagePilotStatus: 'TENTATIVE',
                                    isApprentice: pilotData.isAspiringPilot,
                                }
                            ]
                        }
                        : p
                )
            );

            addSuccessNotification(`Tildelt som tentativ på oppdraget.`);
            setCanAccept(false);
        }
    };

    return (
        <Card
            onClick={handleOnClick}
            id={pilotage.pilotageId}
            title={pilotage.pilotageShip.shipName}
            subTitle={Moment(pilotage.pilotageDetail.fromTime).format('DD.MM HH:mm')}
            borderColor="var(--card-border)"
            showIndicator={true}
            canAccept={canAccept}
            onAccept={() => handleAccept(pilotage.pilotageId)}
            showIndicatorColor="var(--card-border">
            <div className="current-pilotage">
                <div>
                    <div>
                        <div>{pilotage.fromLocation.name}</div>
                        <div>{pilotage.pilotageNo}</div>
                    </div>
                    <div> <ArrowElbowDownRight weight="duotone" size={20} /> </div>
                    <div>{pilotage.toLocation.name}</div>
                </div>
                <div>
                    <Pilots pilotagePilots={pilotage.pilotagePilotsInformation} />
                </div>
                <div>
                    {pilotage.hasHazmat && <SvgIcon iconName="hazmat" dimensions={32} />}
                    {pilotage.hasObservation && <Eye weight="duotone" size={32} />}
                    {pilotage.isTowing && <SvgIcon iconName="towing" dimensions={32} />}
                    {pilotage.isHelicopterRequired && <SvgIcon iconName="helicopter" dimensions={32} />}
                    {pilotage.pilotageDetail.isLocked && <Lock weight="duotone" size={32} />}
                    {pilotage.hasPecExam && <SvgIcon iconName="exam" dimensions={32} />}
                    <ExemptionStatusIcon status={pilotage.pilotageExemptionApplicationStatus} />
                </div>
            </div>
        </Card>
    )

    function handleOnClick() {
        onClick(pilotage);
    }

    function getCanAccept() {
        if (!pilotData?.isAspiringPilot) return false;

        const arr = pilotage.pilotagePilotsInformation.filter(p => p.pilotPersonId === pilotData.personID);
        return isArrayEmpty(arr);;
    }
}

export const ExemptionStatusIcon = ({ status }) => {
    switch (status?.systemName) {
        case PilotageExemptionApplicationStatusEnum.APPLIED: {
            return <SvgIcon iconName="exemptionNotSent" dimensions={30} color={PilotageExemptionApplicationStatusColors.APPLIED} />
        }
        case PilotageExemptionApplicationStatusEnum.CONSIDERED_EVIDENTLY_JUSTIFIABLE: {
            return <SvgIcon iconName="exemptionPending" dimensions={30} color={PilotageExemptionApplicationStatusColors.CONSIDERED_EVIDENTLY_JUSTIFIABLE} />
        }
        case PilotageExemptionApplicationStatusEnum.CONSIDERED_NOT_EVIDENTLY_JUSTIFIABLE: {
            return <SvgIcon iconName="exemptionPending" dimensions={30} color={PilotageExemptionApplicationStatusColors.CONSIDERED_NOT_EVIDENTLY_JUSTIFIABLE} />
        }
        case PilotageExemptionApplicationStatusEnum.DISMISSED: {
            return <SvgIcon iconName="exemptionRejected" dimensions={30} color={PilotageExemptionApplicationStatusColors.DISMISSED} />
        }
        case PilotageExemptionApplicationStatusEnum.GRANTED: {
            return <SvgIcon iconName="exemptionGranted" dimensions={30} color={PilotageExemptionApplicationStatusColors.GRANTED} />
        }
        case PilotageExemptionApplicationStatusEnum.REJECTED: {
            return <SvgIcon iconName="exemptionRejected" dimensions={30} color={PilotageExemptionApplicationStatusColors.REJECTED} />
        }
        case PilotageExemptionApplicationStatusEnum.SENT_TO_PILOT_MASTER: {
            return <SvgIcon iconName="exemptionPending" dimensions={30} color={PilotageExemptionApplicationStatusColors.SENT_TO_PILOT_MASTER} />
        }
        case PilotageExemptionApplicationStatusEnum.VOYAGE_ENDED: {
            return <SvgIcon iconName="exemptionRejected" dimensions={30} color={PilotageExemptionApplicationStatusColors.VOYAGE_ENDED} />
        }
        default: return "";
    }
}

